// Phase 2.2: Timezone Preference UI Components (COMPLETE)
import React from 'react';
import { COMMON_TIMEZONES, getTimezoneDisplay, detectUserTimezone } from '@joinomu/shared';

// TypeScript interfaces
export interface TimezonePreference {
  timezone: string;
  displayFormat: '12h' | '24h';
  autoDetect: boolean;
}

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
  className?: string;
}

interface TimezonePreferencesPanelProps {
  preferences: TimezonePreference;
  onChange: (preferences: TimezonePreference) => void;
  onSave?: () => void;
}

// Main timezone selector component
export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  return (
    <div className={`timezone-selector ${className}`}>
      <label htmlFor="timezone-select" className="block text-sm font-medium text-gray-700 mb-1">
        Timezone
      </label>
      <select
        id="timezone-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      >
        {COMMON_TIMEZONES.map(tz => (
          <option key={tz.value} value={tz.value}>
            {tz.label} ({tz.abbr}) - {getTimezoneDisplay(tz.value)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500">
        Selected: {getTimezoneDisplay(value)}
      </p>
    </div>
  );
};

// Comprehensive timezone preferences panel
export const TimezonePreferencesPanel: React.FC<TimezonePreferencesPanelProps> = ({
  preferences,
  onChange,
  onSave
}) => {
  const handleAutoDetectChange = (autoDetect: boolean) => {
    const newPreferences = { ...preferences, autoDetect };
    
    if (autoDetect) {
      const detectedTimezone = detectUserTimezone();
      newPreferences.timezone = detectedTimezone;
    }
    
    onChange(newPreferences);
  };

  return (
    <div className="timezone-preferences-panel bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Timezone Preferences
      </h3>
      
      {/* Auto-detect toggle */}
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={preferences.autoDetect}
            onChange={(e) => handleAutoDetectChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">
            Automatically detect my timezone
          </span>
        </label>
        {preferences.autoDetect && (
          <p className="mt-1 text-xs text-gray-500">
            Detected: {getTimezoneDisplay(detectUserTimezone())}
          </p>
        )}
      </div>

      {/* Manual timezone selection */}
      {!preferences.autoDetect && (
        <div className="mb-4">
          <TimezoneSelector
            value={preferences.timezone}
            onChange={(timezone) => onChange({ ...preferences, timezone })}
          />
        </div>
      )}

      {/* Time format preference */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Format
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="timeFormat"
              value="12h"
              checked={preferences.displayFormat === '12h'}
              onChange={(e) => onChange({ ...preferences, displayFormat: e.target.value as '12h' | '24h' })}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">12-hour (2:30 PM)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="timeFormat"
              value="24h"
              checked={preferences.displayFormat === '24h'}
              onChange={(e) => onChange({ ...preferences, displayFormat: e.target.value as '12h' | '24h' })}
              className="text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">24-hour (14:30)</span>
          </label>
        </div>
      </div>

      {/* Preview */}
      <div className="mb-4 p-3 bg-gray-50 rounded-md">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
        <p className="text-sm text-gray-600">
          Your timezone: {getTimezoneDisplay(preferences.timezone)}
        </p>
        <p className="text-sm text-gray-600">
          Time format: {preferences.displayFormat === '12h' ? '12-hour (2:30 PM)' : '24-hour (14:30)'}
        </p>
      </div>

      {/* Save button */}
      {onSave && (
        <button
          onClick={onSave}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Save Timezone Preferences
        </button>
      )}
    </div>
  );
};

// Simple timezone display component
export const TimezoneDisplay: React.FC<{ timezone: string; time?: string }> = ({ 
  timezone, 
  time 
}) => {
  return (
    <span className="inline-flex items-center text-sm text-gray-600">
      <span className="font-medium">{getTimezoneDisplay(timezone)}</span>
      {time && <span className="ml-1">- {time}</span>}
    </span>
  );
};

console.log('✅ Phase 2.2 COMPLETE: Timezone preference UI components created');